/**
 * StorefrontSection — custom widget for the "Storefront" section on the Item Detail page.
 *
 * Supports dual-mode rendering:
 * - Modal mode: card click → StorefrontFieldEditModal (toggles always save inline)
 * - Inline mode: slug + description editable in-place; toggles unchanged
 *
 * Follows the iPad Settings group edit pattern:
 *   - Toggle rows save inline (optimistic) via useUpdateDoc — not modal-based
 *   - Text rows (slug, description) open modal in modal mode, or InlineFieldRow in inline mode
 *   - Dimmed state when storefront is OFF
 *   - Auto-slug generation when toggling ON with empty slug
 */

import { useState, useEffect, useCallback } from "react";
import { cn } from "@/lib/utils";
import { Switch } from "@/components/ui/switch";
import { useUpdateDoc } from "@/lib/hooks/useFrappeQuery";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getERPClient } from "@/lib/erp/client";
import { useAuthStore } from "@/lib/store/authStore";
import { toast } from "sonner";
import { slugify, displaySlug } from "./storefrontUtils";
import { useStoreList } from "@/hooks/useStoreList";
import { StorefrontStoreList } from "./StorefrontStoreList";
import { StorefrontFieldEditModal } from "./StorefrontFieldEditModal";
import type { ShopifySectionWidgetProps } from "../types";
import { SectionHeader } from "../../../SectionHeader";
import { ItemCollectionsSection } from "./ItemCollectionsSection";
import { useDetailDensity } from "../../../../hooks/useDetailDensity";
import { useDetailEditMode } from "../../../../hooks/useDetailEditMode";
import { useLanguage } from "@/i18n/useLanguage";

const STOREFRONT_LABELS: Record<string, Record<string, string>> = {
  publishToStorefront: { en: "Publish to Storefront", th: "เผยแพร่ไปยังหน้าร้าน" },
  catalogOnly: { en: "Catalog Only", th: "แค่แสดงในแคตตาล็อก" },
  catalogOnlyDesc: { en: "Visible but not purchasable", th: "แสดงสินค้าแต่ไม่สามารถซื้อได้" },
  urlSlug: { en: "URL Slug", th: "URL ของสินค้า" },
  shortDescription: { en: "Short Description", th: "คำอธิบายสั้น" },
};
import { useInlineFieldSave } from "@/components/inline-edit/detail/useInlineFieldSave";
import { useBufferFieldSave } from "@/components/inline-edit/detail/useBufferFieldSave";
import { useInlineFieldUndo } from "@/components/inline-edit/detail/useInlineFieldUndo";
import { getAdjacentField } from "@/components/inline-edit/detail/getAdjacentField";
import { InlineFieldRow } from "../../../InlineFieldRow";
import { itemQueryKeys } from "@/features/inventory/api/itemQueryKeys";
import type { DocFieldSchema } from "@/lib/erp/meta";

const STOREFRONT_FALLBACK_URL = "yourstore.shop.zaviago.com";

/** Synthetic field schemas for slug + description (not in metaFields) */
const SLUG_FIELD: DocFieldSchema = {
  fieldname: "storefront_slug",
  label: "URL Slug",
  fieldtype: "Data",
} as DocFieldSchema;

const DESCRIPTION_FIELD: DocFieldSchema = {
  fieldname: "storefront_short_description",
  label: "Short Description",
  fieldtype: "Small Text",
} as DocFieldSchema;

export function StorefrontSection({
  formData,
  onFieldChange,
  fieldErrors,
  onClearFieldError,
}: ShopifySectionWidgetProps) {
  const itemName = formData?.name as string | undefined;
  const { language } = useLanguage();
  const sl = (key: string) => STOREFRONT_LABELS[key]?.[language] ?? STOREFRONT_LABELS[key]?.en ?? key;
  const storefrontUrl = useAuthStore((s) => s.storefrontUrl);
  const baseUrl = storefrontUrl || STOREFRONT_FALLBACK_URL;
  const { textSize, rowPx, rowPy, rowFirst, rowGap, dividerMx, rowLabel } = useDetailDensity();

  // Edit mode: mobile forces modal (built into hook)
  const { editMode } = useDetailEditMode();
  const isNew = !formData?.name;

  // Multi-store: per-store switch rows replace the single publish toggle
  const { data: stores = [] } = useStoreList();
  const isMultiStore = stores.length > 1;

  // Inline save: network patch for existing docs, local buffer for new docs
  const inlineSave = useInlineFieldSave({
    doctype: "Item",
    docname: formData.name ?? "",
    detailKey: itemQueryKeys.detail(formData.name ?? ""),
  });
  const bufferSave = useBufferFieldSave();
  const { saveField, getFieldState, retryField, undoField, getUndoable, lastSavedField } = isNew ? bufferSave : inlineSave;

  useInlineFieldUndo({ undoField, getUndoable, lastSavedField });

  const [editingField, setEditingField] = useState<string | null>(null);

  // Local toggle state for optimistic UI
  const enabled = formData?.storefront_enabled === 1 || formData?.storefront_enabled === true;
  const [localEnabled, setLocalEnabled] = useState(enabled);

  useEffect(() => {
    setLocalEnabled(enabled);
  }, [enabled]);

  const { mutateAsync, isPending: isUpdatePending } = useUpdateDoc("Item", itemName || "");
  const queryClient = useQueryClient();

  // Dedicated visibility toggle — updates child table (source of truth for storefronts)
  const { mutateAsync: toggleVisibility, isPending: isTogglePending } = useMutation({
    mutationFn: async (enabled: boolean) => {
      const client = getERPClient();
      return client.call(
        "zaviago_backend.storefront.api_visibility_toggle.toggle_storefront_visibility",
        { item_code: itemName, enabled: enabled ? 1 : 0 },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["list", "Item"] });
    },
  });

  const isPending = isUpdatePending || isTogglePending;

  // Modal state — single modal for both slug + description
  const [modalOpen, setModalOpen] = useState(false);

  const catalogOnly = formData?.storefront_catalog_only === 1 || formData?.storefront_catalog_only === true;
  const [localCatalogOnly, setLocalCatalogOnly] = useState(catalogOnly);

  useEffect(() => {
    setLocalCatalogOnly(catalogOnly);
  }, [catalogOnly]);

  const slug = (formData?.storefront_slug as string) || "";
  const description = (formData?.storefront_short_description as string) || "";

  // --- Toggle handler (inline optimistic save) ---
  const handleToggle = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    const next = !localEnabled;
    setLocalEnabled(next);

    // Auto-slug: when toggling ON with an empty slug, generate from item_name
    let autoSlug: string | null = null;
    if (next && !slug) {
      const itemNameStr = (formData?.item_name as string) || "";
      if (itemNameStr) {
        autoSlug = slugify(itemNameStr);
      }
    }

    // New item: write to buffer only (no network call)
    if (isNew) {
      onFieldChange("storefront_enabled", next ? 1 : 0);
      if (autoSlug) onFieldChange("storefront_slug", autoSlug);
      return;
    }

    const prev = localEnabled;
    try {
      // Update child table visibility (source of truth for storefronts)
      await toggleVisibility(next);

      // Also update auto-slug if needed (separate field-level save)
      if (autoSlug) {
        await mutateAsync({ storefront_slug: autoSlug });
      }

      onFieldChange("storefront_enabled", next ? 1 : 0);
      if (autoSlug) onFieldChange("storefront_slug", autoSlug);
    } catch {
      setLocalEnabled(prev);
      toast.error("Failed to update storefront visibility. Please try again.");
    }
  };

  // --- Catalog Only toggle handler ---
  const handleCatalogOnlyToggle = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    if (!localEnabled) return;

    const next = !localCatalogOnly;
    setLocalCatalogOnly(next);

    // New item: write to buffer only
    if (isNew) {
      onFieldChange("storefront_catalog_only", next ? 1 : 0);
      return;
    }

    const prev = localCatalogOnly;
    try {
      await mutateAsync({ storefront_catalog_only: next ? 1 : 0 });
      onFieldChange("storefront_catalog_only", next ? 1 : 0);
    } catch {
      setLocalCatalogOnly(prev);
      toast.error("Failed to update catalog only setting. Please try again.");
    }
  };

  const STOREFRONT_INLINE_FIELDS = [SLUG_FIELD, DESCRIPTION_FIELD];

  // --- Inline commit handler for slug + description ---
  const handleInlineCommit = async (fieldname: string, newValue: unknown) => {
    const previousValue = formData[fieldname];
    setEditingField(null);
    onFieldChange(fieldname, newValue);
    await saveField(fieldname, newValue, previousValue);
  };

  const handleAdvance = useCallback((fieldname: string, direction: "next" | "prev") => {
    const next = getAdjacentField(STOREFRONT_INLINE_FIELDS, fieldname, direction);
    if (next) setEditingField(next);
  }, []);

  // --- Card click → open group edit modal (modal mode only) ---
  const handleCardClick = () => {
    setModalOpen(true);
  };

  return (
    <>
      <div>
        <SectionHeader label="Storefront" textSize={textSize} />

        <div
          className={cn(
            "rounded-xl border border-border shadow-sm bg-card overflow-hidden",
            editMode === "modal" && "cursor-pointer",
          )}
          onClick={editMode === "modal" ? handleCardClick : undefined}
        >
          <div className="space-y-0">
            {/* Row 1: publish control — per-store switch rows for multi-store
                tenants, the legacy single switch otherwise. Both save inline;
                stopPropagation prevents modal open. */}
            {isMultiStore ? (
              <StorefrontStoreList
                itemName={itemName || ""}
                formData={formData}
                onFieldChange={onFieldChange}
                isNew={isNew}
                rowPx={rowPx}
                rowFirst={rowFirst}
                rowGap={rowGap}
                rowLabel={rowLabel}
                dividerMx={dividerMx}
              />
            ) : (
              <div className={cn(`flex items-center ${rowGap}`, rowPx, rowFirst)}>
                <span className={cn(rowLabel, "w-1/2")} style={{ color: "rgb(17 24 39 / var(--tw-text-opacity, 1))" }}>
                  {sl("publishToStorefront")}
                </span>
                <div className={cn(rowLabel, "w-1/2 flex items-center justify-end")}>
                  <div onClick={(e) => e.stopPropagation()}>
                    <Switch
                      checked={localEnabled}
                      onCheckedChange={() => {}}
                      onClick={handleToggle}
                      disabled={isPending}
                      aria-label="Publish to storefront"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className={cn(dividerMx, "border-t border-border/40")} />

            {/* Row 2: Catalog Only toggle — saves inline in BOTH modes */}
            <div
              className={cn(
                `flex items-center ${rowGap}`, rowPx, rowPy, "transition-opacity",
                localEnabled ? "" : "opacity-50"
              )}
            >
              <div className="w-1/2">
                <span className={rowLabel} style={{ color: "rgb(17 24 39 / var(--tw-text-opacity, 1))" }}>
                  {sl("catalogOnly")}
                </span>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {sl("catalogOnlyDesc")}
                </p>
              </div>
              <div className={cn(rowLabel, "w-1/2 flex items-center justify-end")}>
                <div onClick={(e) => e.stopPropagation()}>
                  <Switch
                    checked={localCatalogOnly}
                    onCheckedChange={() => {}}
                    onClick={handleCatalogOnlyToggle}
                    disabled={isPending || !localEnabled}
                    aria-label="Catalog only mode"
                  />
                </div>
              </div>
            </div>

            <div className={cn(dividerMx, "border-t border-border/40")} />

            {/* Row 3: URL Slug — InlineFieldRow in inline mode, display-only in modal mode */}
            {editMode === "inline" ? (
              <div className={cn("transition-opacity", localEnabled ? "" : "opacity-50")}>
                <InlineFieldRow
                  field={SLUG_FIELD}
                  value={slug}
                  label={sl("urlSlug")}
                  editMode={editMode}
                  isEditing={editingField === "storefront_slug"}
                  onStartEdit={() => setEditingField("storefront_slug")}
                  onCommit={(v) => handleInlineCommit("storefront_slug", v)}
                  onCancel={() => setEditingField(null)}
                  saveState={getFieldState("storefront_slug")}
                  onAdvance={(dir) => handleAdvance("storefront_slug", dir)}
                  onRetry={() => retryField("storefront_slug")}
                  error={fieldErrors?.["storefront_slug"]}
                  onClearError={() => onClearFieldError?.("storefront_slug")}
                  rowPx={rowPx}
                  rowPy={rowPy}
                  rowGap={rowGap}
                  rowLabel={rowLabel}
                  dividerMx={dividerMx}
                  isLast={false}
                />
              </div>
            ) : (
              <>
                <div
                  className={cn(
                    rowPx, rowPy, "transition-opacity",
                    localEnabled ? "" : "opacity-50"
                  )}
                >
                  <div className={`flex items-center ${rowGap}`}>
                    <span className={cn(rowLabel, "w-1/2")} style={{ color: "rgb(17 24 39 / var(--tw-text-opacity, 1))" }}>
                      {sl("urlSlug")}
                    </span>
                    <div className={cn(rowLabel, "w-1/2 text-right text-foreground break-words")}>
                      {slug ? displaySlug(slug) : <span className="text-muted-foreground">{"\u2014"}</span>}
                    </div>
                  </div>
                  {slug && (
                    <p className="mt-1 text-xs font-mono text-muted-foreground truncate">
                      {baseUrl}/product/{displaySlug(slug)}
                    </p>
                  )}
                </div>
                <div className={cn(dividerMx, "border-t border-border/40")} />
              </>
            )}

            {/* Row 4: Short Description — InlineFieldRow in inline mode, display-only in modal mode */}
            {editMode === "inline" ? (
              <div className={cn("transition-opacity", localEnabled ? "" : "opacity-50")}>
                <InlineFieldRow
                  field={DESCRIPTION_FIELD}
                  value={description}
                  label={sl("shortDescription")}
                  editMode={editMode}
                  isEditing={editingField === "storefront_short_description"}
                  onStartEdit={() => setEditingField("storefront_short_description")}
                  onCommit={(v) => handleInlineCommit("storefront_short_description", v)}
                  onCancel={() => setEditingField(null)}
                  saveState={getFieldState("storefront_short_description")}
                  onAdvance={(dir) => handleAdvance("storefront_short_description", dir)}
                  onRetry={() => retryField("storefront_short_description")}
                  error={fieldErrors?.["storefront_short_description"]}
                  onClearError={() => onClearFieldError?.("storefront_short_description")}
                  rowPx={rowPx}
                  rowPy={rowPy}
                  rowGap={rowGap}
                  rowLabel={rowLabel}
                  dividerMx={dividerMx}
                  isLast={false}
                />
              </div>
            ) : (
              <>
                <div
                  className={cn(
                    rowPx, rowPy, "transition-opacity",
                    localEnabled ? "" : "opacity-50"
                  )}
                >
                  <div className={`flex items-center ${rowGap}`}>
                    <span className={cn(rowLabel, "w-1/2")} style={{ color: "rgb(17 24 39 / var(--tw-text-opacity, 1))" }}>
                      {sl("shortDescription")}
                    </span>
                    <div className={cn(rowLabel, "w-1/2 text-right text-foreground")}>
                      {description ? (
                        <span className="line-clamp-2 break-words">{description}</span>
                      ) : (
                        <span className="text-muted-foreground">{"\u2014"}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className={cn(dividerMx, "border-t border-border/40")} />
              </>
            )}

            {/* Collections — inside the same card, unchanged in both modes */}
            {editMode === "modal" && <div className={cn(dividerMx, "border-t border-border/40")} />}
            <div onClick={(e) => e.stopPropagation()}>
              <ItemCollectionsSection
                formData={formData}
                onFieldChange={onFieldChange}
                section={{} as any}
                embedded
              />
            </div>
          </div>
        </div>
      </div>

      {/* Group edit modal — ALL fields: toggles + slug + description (modal mode) */}
      {itemName && (
        <StorefrontFieldEditModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          enabled={localEnabled}
          catalogOnly={localCatalogOnly}
          chatCheckoutEnabled={formData?.storefront_chat_checkout === 1 || formData?.storefront_chat_checkout === true}
          showInFeed={formData?.storefront_show_in_feed === 1 || formData?.storefront_show_in_feed === true}
          feedShowBundleContents={formData?.storefront_feed_show_bundle === 1 || formData?.storefront_feed_show_bundle === true}
          isBundle={formData?.is_product_bundle === 1 || formData?.is_product_bundle === true}
          onToggleEnabled={handleToggle}
          onToggleCatalogOnly={handleCatalogOnlyToggle}
          onToggleChatCheckout={(e) => { e.stopPropagation(); e.preventDefault(); }}
          onToggleShowInFeed={(e) => { e.stopPropagation(); e.preventDefault(); }}
          onToggleFeedShowBundle={(e) => { e.stopPropagation(); e.preventDefault(); }}
          togglePending={isPending}
          currentSlug={slug}
          currentDescription={description}
          storefrontBaseUrl={baseUrl}
          itemName={itemName}
          onFieldChange={onFieldChange}
        />
      )}
    </>
  );
}
